<template>
  <div class="w-full max-w-3xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="rounded-2xl border border-stone-200 bg-white/90 p-4 shadow-sm dark:border-stone-700 dark:bg-stone-900/70">
      <div class="flex flex-wrap items-center justify-between">
        <div>
          <h3 class="font-semibold text-slate-800 dark:text-slate-100">{{ t('agentCreate.questionAnswers.title', 'Add questions and answers') }}</h3>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">{{ t('agentCreate.questionAnswers.description', 'Add the answers your agent should know now, or continue and add them later.') }}</p>
        </div>
        <div class="mt-3 flex flex-wrap md:mt-0">
          <button type="button" class="btn btn-sm" @click="downloadTemplate">
            <Icon icon="tabler:download" width="16" />
            {{ t('agentCreate.questionAnswers.downloadTemplate', 'Download Excel template') }}
          </button>
          <label class="btn btn-sm ml-2">
            <Icon icon="tabler:upload" width="16" />
            {{ t('agentCreate.questionAnswers.uploadExcel', 'Upload Excel') }}
            <input type="file" class="hidden" accept=".xlsx,.xls" @change="importWorkbook" />
          </label>
        </div>
      </div>

      <div v-if="importMessage !== ''" class="alert alert-success mt-4 text-sm">
        <Icon icon="tabler:circle-check" width="18" />
        <span>{{ importMessage }}</span>
      </div>

      <div class="mt-4 overflow-x-auto rounded-xl border border-base-300">
        <table class="table table-sm w-full">
          <thead>
            <tr>
              <th class="w-12">#</th>
              <th>{{ t('agentCreate.questionAnswers.question', 'Question') }}</th>
              <th>{{ t('agentCreate.questionAnswers.answer', 'Answer') }}</th>
              <th class="min-w-40">{{ t('agentCreate.questionAnswers.exactAnswer', 'Use exact answer') }}</th>
              <th class="w-16"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, index) in entries" :key="entry.id">
              <td>{{ index + 1 }}</td>
              <td class="min-w-56">
                <textarea v-model="entry.question" rows="3" class="textarea textarea-bordered w-full" :placeholder="t('agentCreate.questionAnswers.questionPlaceholder', 'Enter a customer question')" @input="emitEntries"></textarea>
              </td>
              <td class="min-w-56">
                <textarea v-model="entry.answer" rows="3" class="textarea textarea-bordered w-full" :placeholder="t('agentCreate.questionAnswers.answerPlaceholder', 'Enter the desired answer')" @input="emitEntries"></textarea>
              </td>
              <td>
                <label class="label cursor-pointer justify-start">
                  <input v-model="entry.forceExactAnswer" type="checkbox" class="checkbox" @change="emitEntries" />
                  <span class="label-text ml-2 text-xs">{{ t('agentCreate.questionAnswers.exactAnswerHint', 'For similar questions') }}</span>
                </label>
              </td>
              <td>
                <button type="button" class="btn btn-error btn-sm" :title="t('common.remove', 'Remove')" @click="removeEntry(index)">
                  <Icon icon="tabler:trash" width="16" />
                </button>
              </td>
            </tr>
            <tr v-if="entries.length === 0">
              <td colspan="5" class="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {{ t('agentCreate.questionAnswers.empty', 'No questions and answers added yet.') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button type="button" class="btn btn-sm mt-4" @click="addEntry">
        <Icon icon="tabler:plus" width="16" />
        {{ t('agentCreate.questionAnswers.add', 'Add question and answer') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import * as XLSX from 'xlsx';
import { useI18n } from '@/i18n';

const emit = defineEmits(['update:entries']);
const { t, currentLanguage, availableLanguages } = useI18n();
const entries = ref([]);
const importMessage = ref('');
let entryCounter = 0;

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

function createEntry(question = '', answer = '', forceExactAnswer = false) {
  entryCounter = entryCounter + 1;
  return {
    id: `question-answer-${entryCounter}`,
    question,
    answer,
    forceExactAnswer: forceExactAnswer === true
  };
}

function addEntry() {
  entries.value = entries.value.concat([createEntry()]);
  emitEntries();
}

function removeEntry(index) {
  entries.value = entries.value.filter((item, itemIndex) => itemIndex !== index);
  emitEntries();
}

function normalizeEntry(entry) {
  const question = typeof entry.question === 'string' ? entry.question.trim() : '';
  const answer = typeof entry.answer === 'string' ? entry.answer.trim() : '';
  return {
    question,
    answer,
    forceExactAnswer: entry.forceExactAnswer === true
  };
}

function getEntries() {
  const validEntries = [];
  let index = 0;
  while (index < entries.value.length) {
    const entry = normalizeEntry(entries.value[index]);
    if (entry.question.length > 0 && entry.answer.length > 0) {
      validEntries.push(entry);
    }
    index = index + 1;
  }
  return validEntries;
}

function emitEntries() {
  emit('update:entries', getEntries());
}

function downloadTemplate() {
  const worksheet = XLSX.utils.json_to_sheet([{
    Question: '',
    Answer: '',
    'Force Exact Answer': 'No'
  }]);
  worksheet['!cols'] = [{ wch: 45 }, { wch: 65 }, { wch: 22 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions and Answers');
  XLSX.writeFile(workbook, 'QuestionAnswerTemplate.xlsx');
}

function readCell(row, names) {
  let index = 0;
  while (index < names.length) {
    const value = row[names[index]];
    if (value !== undefined && value !== null) {
      return String(value).trim();
    }
    index = index + 1;
  }
  return '';
}

function isExactAnswerValue(value) {
  const normalized = String(value === undefined || value === null ? '' : value).trim().toLowerCase();
  return normalized === 'yes' || normalized === 'true' || normalized === '1';
}

function importWorkbook(event) {
  importMessage.value = '';
  const target = event.target;
  if (target === undefined || target === null || target.files === undefined || target.files === null || target.files.length === 0) {
    return;
  }
  const reader = new FileReader();
  reader.onload = (loadEvent) => {
    const result = loadEvent !== undefined && loadEvent !== null && loadEvent.target !== undefined && loadEvent.target !== null ? loadEvent.target.result : null;
    if (result === null) {
      return;
    }
    try {
      const workbook = XLSX.read(new Uint8Array(result), { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = sheetName !== undefined ? workbook.Sheets[sheetName] : undefined;
      const rows = worksheet !== undefined ? XLSX.utils.sheet_to_json(worksheet, { defval: '' }) : [];
      const nextEntries = [];
      let index = 0;
      while (index < rows.length) {
        const row = rows[index];
        const question = readCell(row, ['Question', 'User Question', 'Query', 'שאלה', 'السؤال']);
        const answer = readCell(row, ['Answer', 'Correct Answer', 'Response', 'תשובה', 'الإجابة']);
        const forceExactAnswer = isExactAnswerValue(readCell(row, ['Force Exact Answer', 'Exact Answer', 'Use Exact Answer']));
        if (question.length > 0 && answer.length > 0) {
          nextEntries.push(createEntry(question, answer, forceExactAnswer));
        }
        index = index + 1;
      }
      if (nextEntries.length > 0) {
        entries.value = entries.value.concat(nextEntries);
        importMessage.value = t('agentCreate.questionAnswers.imported', '{count} questions and answers were added.', { count: nextEntries.length });
        emitEntries();
      }
    } catch (error) {
      importMessage.value = '';
    }
  };
  reader.readAsArrayBuffer(target.files[0]);
  target.value = '';
}

defineExpose({
  getEntries,
  isReady: () => true
});
</script>
